import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useEffect,
} from 'react'
import dayjs from 'dayjs'

import type {
  PlayerState,
  PlayerAction,
  AdData,
  VideoPlayerContextType,
  VideoPlayerProviderProps,
  UpNextData,
} from './types'
import type { Dayjs } from 'dayjs'

const playerReducer = (
  state: PlayerState,
  action: PlayerAction
): PlayerState => {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.payload }
    case 'SET_AD_DATA':
      return { ...state, adData: action.payload }
    case 'SET_AD_CURRENT_TIME':
      return state.adData
        ? {
            ...state,
            adData: {
              ...state.adData,
              adCurrentTime: action.payload,
            },
          }
        : state
    case 'SET_CURRENT_PROGRESS':
      return { ...state, currentProgress: action.payload }
    case 'SET_DURATION':
      return { ...state, duration: action.payload }
    case 'SET_PLAYER_STATE':
      return action.payload
    case 'UPDATE_PLAYER_STATE':
      return { ...state, ...action.payload }
    case 'SET_UP_NEXT_DATA':
      return {
        ...state,
        upNextData: action.payload,
      }
    case 'SET_IS_UP_NEXT_VISIBLE':
      return {
        ...state,
        upNextData: state.upNextData
          ? {
              ...state.upNextData,
              isUpNextVisible: action.payload,
            }
          : null,
      }
    case 'SET_LAST_USER_INTERACTION':
      return {
        ...state,
        lastUserInteraction: action.payload,
      }
    case 'SET_IS_USER_TRYING_TO_SEEK':
      return {
        ...state,
        isUserTryingToSeek: action.payload,
      }
    case 'SET_SHOW_CONTROLS':
      return {
        ...state,
        showControls: action.payload,
      }
    default:
      return state
  }
}

const initialPlayerState: PlayerState = {
  status: 'init',
  currentProgress: 0,
  duration: 0,
  upNextData: null,
  lastUserInteraction: dayjs(),
  isUserTryingToSeek: false,
  showControls: false,
}

const VideoPlayerContext = createContext<VideoPlayerContextType>({
  playerState: initialPlayerState,
  playerStateRef: { current: initialPlayerState },
  dispatch: () => {},
  setPlayerStatus: () => {},
  setAdData: () => {},
  setCurrentProgress: () => {},
  setDuration: () => {},
  updatePlayerState: () => {},
  setAdCurrentTime: () => {},
  setUpNextData: () => {},
  setIsUpNextVisible: () => {},
  setLastUserInteraction: () => {},
  setIsUserTryingToSeek: () => {},
  setShowControls: () => {},
})

export const VideoPlayerProvider = ({ children }: VideoPlayerProviderProps) => {
  const [playerState, dispatch] = useReducer(playerReducer, initialPlayerState)
  const playerStateRef = useRef<PlayerState>(initialPlayerState)

  useEffect(() => {
    playerStateRef.current = playerState
  }, [playerState])

  const setPlayerStatus = (status: PlayerState['status']) => {
    dispatch({ type: 'SET_STATUS', payload: status })
  }

  const setAdData = (adData: AdData | undefined) => {
    dispatch({ type: 'SET_AD_DATA', payload: adData })
  }

  const setCurrentProgress = (progress: number) => {
    dispatch({ type: 'SET_CURRENT_PROGRESS', payload: progress })
  }

  const setDuration = (duration: number) => {
    dispatch({ type: 'SET_DURATION', payload: duration })
  }

  const updatePlayerState = (updates: Partial<PlayerState>) => {
    dispatch({ type: 'UPDATE_PLAYER_STATE', payload: updates })
  }

  const setAdCurrentTime = (adCurrentTime: number) => {
    dispatch({ type: 'SET_AD_CURRENT_TIME', payload: adCurrentTime })
  }

  const setUpNextData = (upNextData: UpNextData | null) => {
    dispatch({ type: 'SET_UP_NEXT_DATA', payload: upNextData })
  }

  const setIsUpNextVisible = (isUpNextVisible: boolean) => {
    dispatch({ type: 'SET_IS_UP_NEXT_VISIBLE', payload: isUpNextVisible })
  }

  const setLastUserInteraction = (lastUserInteraction: Dayjs) => {
    dispatch({
      type: 'SET_LAST_USER_INTERACTION',
      payload: lastUserInteraction,
    })
  }

  const setIsUserTryingToSeek = (isUserTryingToSeek: boolean) => {
    dispatch({
      type: 'SET_IS_USER_TRYING_TO_SEEK',
      payload: isUserTryingToSeek,
    })
  }

  const setShowControls = (showControls: boolean) => {
    dispatch({
      type: 'SET_SHOW_CONTROLS',
      payload: showControls,
    })
  }

  return (
    <VideoPlayerContext.Provider
      value={{
        playerState,
        playerStateRef,
        dispatch,
        setPlayerStatus,
        setAdData,
        setCurrentProgress,
        setDuration,
        updatePlayerState,
        setAdCurrentTime,
        setUpNextData,
        setIsUpNextVisible,
        setLastUserInteraction,
        setIsUserTryingToSeek,
        setShowControls,
      }}
    >
      {children}
    </VideoPlayerContext.Provider>
  )
}

export const useVideoPlayerData = () => {
  const context = useContext(VideoPlayerContext)
  if (context === undefined) {
    throw new Error('useVideoPlayer must be used within a VideoPlayerProvider')
  }
  return context
}
